import type { ColumnDef } from "@tanstack/react-table";
import { DataTable } from "@/components/ui/data-table";
import { TableCard } from "@/components/ui/table-card";
import { UserAvatar } from "@/components/common/UserAvatar";
import { BrandPayoutRequest } from "../../../types/payouts";
import { mockPendingPayouts } from "../../../mocks/payoutData";
import { formatCurrency } from "../../../utils/currency";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns: ColumnDef<BrandPayoutRequest, any>[] = [
  {
    id: "name",
    header: "Name",
    cell: ({ row }) => (
      <div className="flex items-center gap-3 min-w-0">
        <UserAvatar
          src={row.original.avatar}
          name={row.original.name}
          className="w-8 h-8 shrink-0 text-xs"
        />
        <span className="break-words font-medium">{row.original.name}</span>
      </div>
    ),
    meta: {
      width: "w-48",
      skeleton: (
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-muted animate-pulse" />
          <div className="h-3.5 w-28 rounded bg-muted animate-pulse" />
        </div>
      ),
    },
  },
  {
    id: "username",
    header: "Username",
    cell: ({ row }) => (
      <span className="block break-all text-muted-foreground">
        {row.original.username}
      </span>
    ),
    meta: { width: "w-36" },
  },
  {
    id: "payoutStatus",
    header: "Payout status",
    cell: ({ row }) => {
      const statusColors = {
        Pending: "text-yellow-600 bg-yellow-50",
        Processing: "text-blue-600 bg-blue-50",
        Completed: "text-green-600 bg-green-50",
      };

      return (
        <span
          className={`px-2 py-1 rounded-full text-sm ${
            statusColors[row.original.payoutStatus]
          }`}
        >
          {row.original.payoutStatus}
        </span>
      );
    },
    meta: { width: "w-28" },
  },
  {
    id: "email",
    header: "Email address",
    cell: ({ row }) => (
      <span className="block break-all">{row.original.email}</span>
    ),
    meta: { width: "w-48" },
  },
  {
    id: "paymentMethod",
    header: "Payment method",
    cell: ({ row }) => row.original.paymentMethod,
    meta: { width: "w-32" },
  },
  {
    id: "paymentInformation",
    header: "Payment information",
    cell: ({ row }) => (
      <span className="block break-all">{row.original.paymentInformation}</span>
    ),
    meta: { width: "w-56" },
  },
  {
    id: "code",
    header: "Code",
    cell: ({ row }) => row.original.code,
    meta: { width: "w-24" },
  },
  {
    id: "amount",
    header: "Amount",
    cell: ({ row }) => <span>{formatCurrency(Number(row.original.amount) || 0)}</span>,
    meta: { width: "w-24" },
  },
];

export function PendingPayouts() {
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">
        Pending payouts
      </h1>

      <div>
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Actions:{" "}
            <button className="text-muted-foreground hover:text-foreground">
              Select All
            </button>
            /
            <button className="text-muted-foreground hover:text-foreground">
              Unselect
            </button>
          </div>
        </div>
      </div>

      <TableCard>
        <DataTable
          columns={columns}
          data={mockPendingPayouts}
          emptyText="No pending payouts"
          getRowId={(row) => row.id}
        />
      </TableCard>
    </div>
  );
}
